"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

/**
 *
 * This component traps and disabled the back and forward browser button clicks.
 *
 * How it works:
 * - Pushes two dummy history states on mount.
 * - When the user clicks Back or Forward, `popstate` is triggered.
 * - We immediately call `history.forward()` to keep the user stuck.
 *
 * This makes the navigation buttons look clickable but effectively disabled.
 */

const PreventBrowserNavigation = () => {
  const pathname = usePathname();

  useEffect(() => {
    /**
     * Step 1: Push two dummy states into the browser history stack.
     * This ensures that there is always at least one "trap" state
     * to bounce back to, so `popstate` keeps firing on back/forward.
     * This is required to get latest trap state
     */
    window.history.pushState({ trap: true }, "", window.location.href);
    window.history.pushState({ trap: true }, "", window.location.href);

    /**
     * Step 2: Define the handler that blocks navigation.
     * If the current history entry has our `trap` flag,
     * we immediately call `history.forward()` to return
     * the user to the trapped state.
     *
     * This ensures that clicking the Back or Forward button
     * does not actually move away from the current page.
     */
    const blockNavigation = () => {
      if (window.history.state?.trap) {
        /**
         * Immediately move forward to cancel the back navigation
         * */
        window.history.forward();
      }
    };

    /**
     * Step 3: Listen for the `popstate` event.
     * This event fires whenever the active history entry changes,
     * such as when the user clicks Back or Forward.
     */
    window.addEventListener("popstate", blockNavigation);

    /**
     * Step 4: Cleanup on unmount.
     * Remove the event listener when the component unmounts
     * or when the route (`pathname`) changes.
     */
    return () => {
      window.removeEventListener("popstate", blockNavigation);
    };
  }, [pathname]);

  return null;
};

export default PreventBrowserNavigation;
